import moment from 'moment-timezone';

/**
 * Interface for date range
 */
export interface DateRange {
  from: Date | null;
  to: Date | null;
}

/**
 * Converts a date stored in UTC (representing org timezone) to a local Date
 * with the same calendar date for display in date pickers
 *
 * @param date - The UTC date to convert
 * @param timezone - The organization timezone (e.g., 'US/Eastern')
 * @returns A local Date object with the same calendar date
 */
export const convertOrgTimezoneToLocalDate = (
  date: Date | null,
  timezone: string | undefined
): Date | null => {
  if (!date || !timezone) {
    return date;
  }

  // Convert the stored UTC date to show the correct calendar date in org timezone
  const m = moment.tz(date, timezone);
  // Create a local Date with the same calendar date
  return new Date(m.year(), m.month(), m.date());
};

/**
 * Converts a date range from org timezone to local dates for display
 *
 * @param dateRange - The date range with UTC dates representing org timezone
 * @param timezone - The organization timezone
 * @returns Date range with local dates matching the org timezone calendar dates
 */
export const convertDateRangeForDisplay = (
  dateRange: DateRange,
  timezone: string | undefined
): DateRange => {
  return {
    from: convertOrgTimezoneToLocalDate(dateRange.from, timezone),
    to: convertOrgTimezoneToLocalDate(dateRange.to, timezone),
  };
};

/**
 * Converts a local date from the date picker to org timezone for storage/backend
 * Preserves the calendar date and interprets it in the org timezone
 *
 * @param date - The local date from the picker
 * @param timezone - The organization timezone
 * @param endOfDay - Whether to set to end of day (23:59:59) instead of start (00:00:00)
 * @returns Date object representing the calendar date in org timezone (stored as UTC)
 */
export const convertLocalDateToOrgTimezone = (
  date: Date,
  timezone: string,
  endOfDay: boolean = false
): Date => {
  // Extract the calendar date components from the local Date object
  const year = date.getFullYear();
  const month = date.getMonth();
  const day = date.getDate();

  // Create a moment in the org timezone using the calendar date
  const m = moment.tz([year, month, day], timezone);

  // Set to start or end of day in org timezone
  return endOfDay ? m.endOf('day').toDate() : m.startOf('day').toDate();
};

/**
 * Handles date range changes from a date picker and converts to org timezone
 *
 * @param newRange - The new date range from the picker
 * @param timezone - The organization timezone
 * @param getDefaultDateRange - Function to get default date range when cleared
 * @returns Object with the processed date range and logs for debugging
 */
export const handleDateRangeChange = (
  newRange: DateRange,
  timezone: string | undefined,
  getDefaultDateRange?: () => DateRange
): {
  dateRange: DateRange;
  logs: {
    input: DateRange;
    calendarDates: { from: string; to: string } | null;
    output: DateRange;
    formatted: { from: string; to: string } | null;
  };
} => {
  const logs = {
    input: newRange,
    calendarDates: null as { from: string; to: string } | null,
    output: { from: null, to: null } as DateRange,
    formatted: null as { from: string; to: string } | null,
  };

  // If both dates are null (Clear button clicked), use default range
  if (!newRange.from && !newRange.to) {
    const defaultRange = getDefaultDateRange?.() || { from: null, to: null };
    logs.output = defaultRange;
    return { dateRange: defaultRange, logs };
  }

  // If timezone is missing or dates are incomplete, return as-is
  if (!timezone || !newRange.from || !newRange.to) {
    logs.output = newRange;
    return { dateRange: newRange, logs };
  }

  // Extract calendar date components
  const fromYear = newRange.from.getFullYear();
  const fromMonth = newRange.from.getMonth();
  const fromDay = newRange.from.getDate();

  const toYear = newRange.to.getFullYear();
  const toMonth = newRange.to.getMonth();
  const toDay = newRange.to.getDate();

  logs.calendarDates = {
    from: `${fromYear}-${String(fromMonth + 1).padStart(2, '0')}-${String(fromDay).padStart(2, '0')}`,
    to: `${toYear}-${String(toMonth + 1).padStart(2, '0')}-${String(toDay).padStart(2, '0')}`,
  };

  // Convert to org timezone
  const fromDate = convertLocalDateToOrgTimezone(newRange.from, timezone, false);
  const toDate = convertLocalDateToOrgTimezone(newRange.to, timezone, true);

  const processedRange = {
    from: fromDate,
    to: toDate,
  };

  logs.output = processedRange;
  logs.formatted = {
    from: moment.tz(fromDate, timezone).format('YYYY-MM-DD HH:mm:ss Z'),
    to: moment.tz(toDate, timezone).format('YYYY-MM-DD HH:mm:ss Z'),
  };

  return { dateRange: processedRange, logs };
};

/**
 * Gets default date range (1 month) in organization timezone
 * Returns dates from start of 1 month ago to end of today
 *
 * @param timezone - Organization timezone (e.g., 'America/New_York')
 * @param minDate - Optional minimum allowed date (e.g., for project start date)
 * @returns Object with from and to dates
 */
export const getDefaultDateRange = (
  timezone: string = 'UTC',
  minDate?: Date | null
): { from: Date; to: Date } => {
  // Get current time in organization timezone
  const now = moment.tz(timezone);
  const oneMonthAgo = now.clone().subtract(1, 'month').startOf('day');

  // If minDate is provided, use it if oneMonthAgo is before minDate
  let fromDate = oneMonthAgo;
  if (minDate) {
    const minMoment = moment.tz(minDate, timezone).startOf('day');
    fromDate = oneMonthAgo.isBefore(minMoment) ? minMoment : oneMonthAgo;
  }

  return {
    from: fromDate.toDate(),
    to: now.endOf('day').toDate(), // Set to END of day, not start
  };
};

/**
 * Gets default date range with June 1, 2025 as minimum date
 * This is used for dashboards that only have data from June 2025 onwards
 *
 * @param timezone - Organization timezone
 * @returns Object with from and to dates
 */
export const getDefaultDateRangeFromJune2025 = (
  timezone: string = 'UTC'
): { from: Date; to: Date } => {
  // Create June 1, 2025 date as the minimum allowed date
  const minDate = moment.tz([2025, 5, 1], timezone).startOf('day').toDate(); // June is month 5 (0-indexed)

  return getDefaultDateRange(timezone, minDate);
};
